import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { useDebounce } from "use-debounce";
import PulseLoader from "react-spinners/PulseLoader";
import { DropdownWithSearch, TextInput, Image } from "hesus-ui-kit";
import { getDateFormat } from "@utils/datePickerUtils";
import QuoteAction from "./components/QuoteAction";
import QuoteStatus from "./components/QuoteStatus";
import QuoteServices from "./components/QuoteServices/QuoteServices";
import toggelDownIcon from "@icons/ic-down-toggle.svg";
import toggelUpIcon from "@icons/ic-up-toggle.svg";
import { loadAllQuotes } from "./QuoteList.actionCreators";
import { setAllQuotesLoadingState } from "./QuoteList.slice";
import { searchInputStyles } from "@utils/extra-style";

import { QUOTES_LIST_ENDPOINT, quoteTypeOptions } from "@utils/constants";
import { renderPriceWithCurrencyWithoutUnit } from "@utils/utils";
import {
  OuterWrapper,
  FlexRow,
  FilterFlexRow,
  SectionContainer,
  GridRow,
  ProductContainer,
  FilterOuter,
  GlobalSearch,
  InfoContainer,
  InfoContainerMargin,
  NoQuote,
  ButtonWrapper,
  CreateButton,
  DunningDateSpan,
  DueDateSpan,
  GrayPara,
  GridLoaderItem,
} from "./QuoteList.styled";

import "./QuoteList.scss";

const ViewData = (props) => {
  const {
    t,
    isRefreshList,
    entityId,
    createQuote,
    setIsRefeshSiteData,
    countryId,
    dependancies,
    jobsiteNeedKickoff,
  } = props;

  const dispatch = useDispatch();
  const history = useNavigate();
  const [quoteData, setQuoteData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageStart, setPageStart] = useState(0);
  const [pageSize, setPageSize] = useState(-1);
  const [search, setSearch] = useState("");
  const [sort, setSortOption] = useState({ dir: "asc", column: "step" });
  const [requestData, setRequestData] = useState({});
  const [statusType, setStatusType] = useState([]);
  const [statusTypeFilter, setStatusTypeFilter] = useState([]);
  const [quotIds, setQuotIds] = useState([]);
  const [displayList, setDisplayList] = useState(false);
  const dataURL = QUOTES_LIST_ENDPOINT;

  const [query, setQuery] = useState(search);
  const [debounceQuery] = useDebounce(query, 700);

  const quoteListByIds = useSelector((state) => state.quoteList.byId);
  const quoteListAllIds = useSelector((state) => state.quoteList.allIds);
  const isQuoteListLoading = useSelector((state) => state.quoteList.isLoading);
  const filteredRecords = useSelector(
    (state) => state.quoteList.filteredRecords
  );

  useEffect(() => {
    setLoading(isQuoteListLoading);
  }, [isQuoteListLoading]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  const handleChange = (e) => {
    setQuery(e.target.value || undefined);
  };

  useEffect(() => {
    setSearch(query);
    setCurrentPage(1);
  }, [debounceQuery]);

  const globalSearchFilter = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const onQuoteTypeFilterChange = (statusValue) => {
    setStatusType(statusValue);
    let typeFilter = [];
    statusValue.map((item) => {
      item.value !== "*" && typeFilter.push(item.value);
    });
    setStatusTypeFilter(typeFilter);
  };
  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    search,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({ pageSize, pageStart, search, sort, appliedFilters });
    // Set the loading state
    setLoading(true);
    const selectedCountries = [countryId];
    const salesman = [];
    dispatch(setAllQuotesLoadingState());
    dispatch(
      loadAllQuotes({
        dataURL,
        pageSize,
        currentPage,
        sort,
        search,
        appliedFilters,
        status: 1,
        dependancies,
        salesman,
        selectedCountries,
      })
    );
  };

  useEffect(() => {
    const quoteSiteId = quoteData.length > 0 ? quoteData[0].site?.id : null;
    if (entityId === quoteSiteId) setDisplayList(true);
    else setDisplayList(false);
  }, [quoteData]);

  useEffect(() => {
    if (!loading) {
      fetchRecords({
        pageSize,
        pageStart,
        search,
        sort,
        dependancies,
        appliedFilters: {
          status: statusTypeFilter,
          siteId: entityId,
        },
        currentPage,
      });
    }
  }, [
    currentPage,
    pageSize,
    pageStart,
    statusTypeFilter,
    search,
    isRefreshDataAgain,
  ]);

  useEffect(() => {
    if (quoteListAllIds.length > 0) {
      const responseData = quoteListAllIds.map((id) => {
        const tempData = { ...quoteListByIds[id] };
        return tempData;
      });
      setQuoteData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setQuoteData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, quoteListAllIds, quoteListByIds, filteredRecords]);

  const displayToggle = (id) => {
    const list = [...quotIds];
    if (quotIds.indexOf(id) === -1) {
      list.push(id);
    } else {
      const idIndex = list.indexOf(id);
      if (idIndex >= 0) {
        list.splice(idIndex, 1);
      }
    }
    setQuotIds(list);
  };

  return (
    <>
      <OuterWrapper>
        <FlexRow>
          <SectionContainer>
            <div>
              <FilterFlexRow>
                <FilterOuter>
                  <DropdownWithSearch
                    label={t("QUOTES_FILTER_STATUS")}
                    placeholder={t("QUOTES_FILTER_STATUS")}
                    name="quote-type-filter"
                    labelClass="quote-list__quote-status-label"
                    containerClass="quote-list__quote-status-dropdown-container"
                    containerStyles={{ minWidth: "12rem" }}
                    value={statusType || ""}
                    defaultOption="Nothing Selected"
                    options={quoteTypeOptions(t)}
                    onChange={onQuoteTypeFilterChange}
                  />
                </FilterOuter>
                <GlobalSearch>
                  <div className="search-box">
                    <TextInput
                      id="quote-list-search"
                      type="search"
                      inputClass="search-box-default-input"
                      value={query || ""}
                      onChange={(e) => handleChange(e)}
                      placeholder=""
                      name="global-filter"
                      inputStyles={searchInputStyles}
                    />
                  </div>
                </GlobalSearch>
                <ButtonWrapper>
                  <CreateButton onClick={createQuote}>{`${t(
                    "ACTION_NEW_QUOTE"
                  )}`}</CreateButton>
                </ButtonWrapper>
              </FilterFlexRow>
            </div>
            <div>
              {loading && (
                <GridRow column="1fr">
                  <GridLoaderItem>
                    <PulseLoader width="100%" color="#1bace4" />
                  </GridLoaderItem>
                </GridRow>
              )}
              <GridRow>
                {quoteData?.length !== "" &&
                  displayList &&
                  quoteData?.map((data, index) => {
                    return (
                      <ProductContainer
                        key={index}
                        opacity={data?.status !== 5 ? 1 : "0.2"}
                      >
                        <GridRow
                          column={
                            data?.quoteItems?.length !== 0 ? "auto 96%" : "100%"
                          }
                          gap="0rem"
                        >
                          {data?.quoteItems?.length !== 0 && (
                            <Image
                              src={
                                quotIds.indexOf(data.id) !== -1
                                  ? toggelUpIcon
                                  : toggelDownIcon
                              }
                              alt={t("QUOTE_ITEMS")}
                              title={t("QUOTE_ITEMS")}
                              className="quote-listing__image-icon"
                              onClick={() => displayToggle(data.id)}
                            />
                          )}
                          <div>
                            <GridRow
                              column="1fr auto auto"
                              gap="0rem"
                              marginBottom="5px"
                            >
                              <Link
                                id={`quote-id${data.id}`}
                                className="quote-listing__quote-name-link"
                                to={`/site/view/${data.site?.id}/quote-view-${data.id}`}
                              >
                                {data?.id + " - " + t(data?.name)}
                              </Link>
                              <QuoteStatus
                                status={data?.status}
                                isActive={data?.isActive}
                              />
                              <QuoteAction
                                quoteId={data.id}
                                siteId={data.site.id}
                                status={data?.status ?? 1}
                                isActive={data?.isActive}
                                onRefreshData={setRefreshState}
                                setIsRefeshSiteData={setIsRefeshSiteData}
                                countryId={countryId}
                                pageType="view"
                                jobsiteNeedKickoff={jobsiteNeedKickoff}
                              />
                            </GridRow>

                            <GridRow
                              column="1fr auto auto"
                              gap="0rem"
                              marginBottom="5px"
                            >
                              <InfoContainer>
                                {data?.quoteItems?.length === 0 && (
                                  <InfoContainer>
                                    <GrayPara>
                                      {t("SITE_NO_QUOTES_ITEMS")}
                                    </GrayPara>
                                  </InfoContainer>
                                )}
                                {data.status === 2 && (
                                  <>
                                    <DueDateSpan
                                      color="#e83f1e"
                                      fontStyle="normal"
                                    >
                                      <DunningDateSpan>
                                        {t("QUOTE_DUNNING_ON")}
                                      </DunningDateSpan>{" "}
                                      &nbsp;
                                      {data.dunningDate
                                        ? " " +
                                          getDateFormat(
                                            data.dunningDate,
                                            "YYYY-MM-DD"
                                          )
                                        : "?"}
                                      {data.followupComment
                                        ? " : " + data.followupComment
                                        : ""}
                                    </DueDateSpan>
                                  </>
                                )}
                                {data.status === 3 && (
                                  <>
                                    <DueDateSpan
                                      color="#767676"
                                      fontStyle="italic"
                                    >
                                      {t("QUOTE_VALIDATION_DATE") +
                                        " " +
                                        getDateFormat(
                                          data.statusUpdatedAt,
                                          "YYYY-MM-DD"
                                        )}
                                    </DueDateSpan>
                                  </>
                                )}
                                {(data.status === 6 || data.status === 7) && (
                                  <>
                                    <DueDateSpan
                                      color="#767676"
                                      fontStyle="italic"
                                    >
                                      {t("QUOTE_LOST_DATE") +
                                        " " +
                                        getDateFormat(
                                          data.statusUpdatedAt,
                                          "YYYY-MM-DD"
                                        )}
                                    </DueDateSpan>
                                  </>
                                )}
                              </InfoContainer>
                              <InfoContainerMargin>
                                <DueDateSpan color="#0A4986" fontStyle="normal">
                                  <strong>{t("QUOTE_MARGIN")}</strong> {" : "}
                                  {data.totalMargin + "%"}
                                </DueDateSpan>
                              </InfoContainerMargin>
                              <InfoContainerMargin>
                                <DueDateSpan color="#0A4986" fontStyle="normal">
                                  <strong>{t("QUOTE_TOTAL")}</strong> {" : "}
                                  {data.currency?.symbol
                                    ? renderPriceWithCurrencyWithoutUnit(
                                        data.totalExcludingTax,
                                        data.currency.symbol,
                                        data.currency.position
                                      )
                                    : data.totalExcludingTax}
                                </DueDateSpan>
                              </InfoContainerMargin>
                            </GridRow>
                          </div>
                        </GridRow>
                        <GridRow>
                          <QuoteServices
                            quoteService={data}
                            quoteCurrency={data.currency}
                            quotIds={quotIds}
                            quotId={data?.id}
                          />
                        </GridRow>
                      </ProductContainer>
                    );
                  })}
              </GridRow>
              {(quoteData?.length === "" || quoteData?.length === 0) && (
                <GridRow column="1fr">
                  <NoQuote>{t("SITE_NO_QUOTES")}</NoQuote>
                </GridRow>
              )}
            </div>
          </SectionContainer>
        </FlexRow>
      </OuterWrapper>
    </>
  );
};

ViewData.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  dependancies: PropTypes.string,
};

ViewData.defaultProps = {
  dependancies: "",
};

export default withTranslation()(ViewData);
